/**
 * Classification catalogs — API contract.
 *
 * The three reference tables the EVM classifier consults besides templates:
 * 4-byte method signatures, bridge contracts and NFT marketplaces. Edited from
 * the super-admin classification workspace; every mutation reloads the
 * in-memory registries of every backend process.
 */

/** Allowed by the `method_signatures_category_check` constraint. */
export const METHOD_SIGNATURE_CATEGORIES = [
  'swap',
  'weth',
  'staking',
  'lending',
  'approval',
] as const
export type ClassificationMethodSignatureCategory = (typeof METHOD_SIGNATURE_CATEGORIES)[number]

/** Allowed by the `bridge_contracts_direction_check` constraint. */
export const BRIDGE_CONTRACT_DIRECTIONS = ['in', 'out', 'both'] as const
export type ClassificationBridgeDirection = (typeof BRIDGE_CONTRACT_DIRECTIONS)[number]

export type BlockchainClassificationCatalogKind =
  | 'method-signatures'
  | 'bridge-contracts'
  | 'nft-marketplaces'

export interface ClassificationMethodSignature {
  id: string
  /** Lower-case `0x` + 8 hex digits. */
  selector: string
  name: string
  /** Engine classification the selector maps to (e.g. `swap`, `wrap`). */
  classification: string
  protocol: string | null
  category: ClassificationMethodSignatureCategory
  enabled: boolean
  updatedAt: string
}

export interface ClassificationBridgeContract {
  id: string
  networkSlug: string
  /** Lower-case `0x` address. */
  contractAddress: string
  name: string
  direction: ClassificationBridgeDirection
  destinationChain: number | null
  enabled: boolean
  updatedAt: string
}

export interface ClassificationNftMarketplace {
  id: string
  networkSlug: string
  contractAddress: string
  name: string
  marketplaceFamily: string | null
  enabled: boolean
  updatedAt: string
}

/** Allowed by the `classification_attribute_map_fiscal_implication_check` constraint. */
export const CLASSIFICATION_FISCAL_IMPLICATIONS = [
  'permuta_patrimonial',
  'rendimiento_capital',
  'no_fiscal',
  'requires_review',
  'depends_context',
] as const
export type ClassificationFiscalImplicationValue =
  (typeof CLASSIFICATION_FISCAL_IMPLICATIONS)[number]

export interface ClassificationAttributeMappingValue {
  /** `transaction_attributes.key`; null = the classification writes no ledger row. */
  transactionAttribute: string | null
  fiscalImplication: string
}

export interface ClassificationAttributeMapEntry extends ClassificationAttributeMappingValue {
  classification: string
  /** What ships with the platform; `edited` is true when the row differs from it. */
  builtin: ClassificationAttributeMappingValue
  edited: boolean
  /** What the importing wallet can observe; null when the engine does not declare it. */
  naturalFlow: { incoming: boolean; outgoing: boolean } | null
  /** Incoherence of the mapping in force (same check as the issues panel). */
  incoherence:
    | { code: 'map_income_attribute_on_outgoing_flow' }
    | { code: 'map_attribute_requires_missing_leg'; missingLeg: 'incoming' | 'outgoing' }
    | null
  /** Leg-0 staged rows with this classification inside the requested window. */
  affectedTransactionCount: number
  updatedAt: string | null
}

export interface ClassificationAttributeOption {
  key: string
  requiresIncoming: boolean
  requiresOutgoing: boolean
  taxEventType: string | null
}

export interface ClassificationAttributeMap {
  windowDays: number
  entries: ClassificationAttributeMapEntry[]
  attributes: ClassificationAttributeOption[]
}

export interface UpdateClassificationAttributeMappingBody
  extends ClassificationAttributeMappingValue {
  /** Required to save a mapping that produces an incoherence. */
  acknowledgeIncoherence?: boolean
}
